import { Component, computed, effect, inject, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormCheckboxComponent, FormInputComponent, FormMultiSelectComponent, FormRadioButtonComponent, FormTextareaComponent } from '@shared/forms';
import { MediaImageComponent, UnavailableComponent } from '@shared/components';
import { PackageFacade } from '@app/store/package';
import { PackageAvailabilityType } from '@app/store/shared/models';
import { FormOptionsFacade } from '@app/store/form-options';
import { MediaSelectionService, MediaTarget } from '@app/dialogs';
import { getAvailabilityOptions } from '../list/config/availability-options';
import { PricingConfiguratorComponent } from '../pricing-configurator/pricing-configurator.component';
import { createCustomFormArray, createTierFormArray, customGroupToValue, tierGroupToValue, CustomFormGroup, PricingEntity } from '../shared';

@Component({
    selector: 'app-package-edit-modal',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.css'],
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        ButtonModule,
        FormInputComponent,
        FormTextareaComponent,
        FormRadioButtonComponent,
        FormMultiSelectComponent,
        FormCheckboxComponent,
        MediaImageComponent,
        UnavailableComponent,
        PricingConfiguratorComponent,
    ],
})
export class EditComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly packageFacade = inject(PackageFacade);
    private readonly formOptionsFacade = inject(FormOptionsFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly mediaSelectionService = inject(MediaSelectionService);
    private readonly translateService = inject(TranslateService);

    readonly package = this.packageFacade.package;
    readonly loading = this.packageFacade.loading;
    readonly saving = this.packageFacade.saving;
    readonly error = this.packageFacade.error;

    readonly availabilityOptions = getAvailabilityOptions(this.translateService);
    readonly courseOptions = computed(() => this.formOptionsFacade.courses());
    readonly pathwayOptions = computed(() => this.formOptionsFacade.predefinedPathways());
    readonly schoolOptions = computed(() => this.formOptionsFacade.schools());
    readonly organisationOptions = computed(() => this.formOptionsFacade.organisations());

    readonly form = this.formBuilder.group({
        name: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        description: this.formBuilder.control(''),
        thumbnailMediaId: this.formBuilder.control<string | null>(null),
        availabilityType: this.formBuilder.control<PackageAvailabilityType>('general', [FormValidators.Required]),
        courseIds: this.formBuilder.control<string[]>([], [FormValidators.Required]),
        pathwayIds: this.formBuilder.control<string[]>([]),
        assignedSchoolIds: this.formBuilder.control<string[]>([]),
        assignedOrganisationIds: this.formBuilder.control<string[]>([]),
        pricingList: createTierFormArray([]),
        customPricingList: this.formBuilder.array<CustomFormGroup>([]),
        isPurchasable: this.formBuilder.control(false),
    });

    private readonly availabilityType = toSignal(this.form.controls.availabilityType.valueChanges, { initialValue: this.form.controls.availabilityType.value });
    readonly isRestricted = computed(() => this.availabilityType() === 'restricted');
    readonly pricingEntities = computed(() => [
        ...this.schoolOptions().map<PricingEntity>(({ label, value }) => ({ id: value, name: label, type: 'school' })),
        ...this.organisationOptions().map<PricingEntity>(({ label, value }) => ({ id: value, name: label, type: 'organisation' })),
    ]);

    constructor() {
        const id = this.route.parent?.snapshot.params['id'];
        if (id) {
            this.packageFacade.getPackage(id);
        }

        if (this.formOptionsFacade.coursesLoaded() !== true) {
            this.formOptionsFacade.getCourses();
        }
        if (this.formOptionsFacade.predefinedPathwaysLoaded() !== true) {
            this.formOptionsFacade.getPredefinedPathways();
        }
        if (this.formOptionsFacade.schoolsLoaded() !== true) {
            this.formOptionsFacade.getSchools();
        }
        if (this.formOptionsFacade.organisationsLoaded() !== true) {
            this.formOptionsFacade.getOrganisations();
        }

        effect(() => {
            const _package = this.package();
            if (!_package) return;

            this.form.patchValue({
                name: _package.name,
                description: _package.description,
                thumbnailMediaId: _package.thumbnailMediaId,
                availabilityType: _package.availabilityType,
                courseIds: _package.courseIds,
                pathwayIds: _package.pathwayIds,
                assignedSchoolIds: _package.assignedSchoolIds,
                assignedOrganisationIds: _package.organisationIds,
                isPurchasable: _package.isPurchasable,
            });

            this.form.setControl('pricingList', createTierFormArray(_package.pricingList));
            this.form.setControl('customPricingList', createCustomFormArray(_package.customPricingList));
        });

        const formEvents = toSignal(this.form.events, { initialValue: null });

        effect(() => {
            formEvents();
            const pricingList = this.form.controls.pricingList;
            const isPurchasable = this.form.controls.isPurchasable;
            const canPurchase = pricingList.length > 0 && pricingList.errors === null;

            if (canPurchase) {
                if (isPurchasable.disabled) {
                    isPurchasable.enable({ emitEvent: false });
                }
            } else {
                if (isPurchasable.value !== false) {
                    isPurchasable.setValue(false, { emitEvent: false });
                }
                if (isPurchasable.enabled) {
                    isPurchasable.disable({ emitEvent: false });
                }
            }
        });

        effect(() => {
            const control = this.form.controls.assignedSchoolIds;

            if (this.isRestricted()) {
                control.setValidators([FormValidators.Required]);
            } else {
                control.clearValidators();
            }

            control.updateValueAndValidity({ emitEvent: false });
        });

        effect(() => {
            if (this.packageFacade.saved()) {
                this.close();
            }
        });

        effect(() => {
            if (this.packageFacade.saving() === false && this.packageFacade.saved() === false) {
                this.form.enable();
            }
        });

        effect(() => {
            const selection = this.mediaSelectionService.selected();
            if (!selection) return;

            if (selection.target === 'thumbnailMediaId') {
                this.form.controls.thumbnailMediaId.setValue(selection.media.id);
                this.mediaSelectionService.clear();
            }
        });
    }

    ngOnDestroy(): void {
        this.packageFacade.clear();
    }

    onSelectThumbnail(): void {
        const target: MediaTarget = 'thumbnailMediaId';
        this.router.navigate([{ outlets: { dialog: ['select-media', { mediaTarget: target, mediaType: 'image' }] } }]);
    }

    removeThumbnail(): void {
        this.form.controls.thumbnailMediaId.setValue(null);
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        const _package = this.package();
        const value = this.form.getRawValue();

        if (this.form.valid && _package) {
            this.packageFacade.updatePackage(_package.id, {
                name: value.name,
                description: value.description,
                thumbnailMediaId: value.thumbnailMediaId,
                originalThumbnailMediaId: _package.thumbnailMediaId,
                availabilityType: value.availabilityType,
                courseIds: value.courseIds,
                pathwayIds: value.pathwayIds,
                assignedSchoolIds: value.assignedSchoolIds,
                customPricingList: this.form.controls.customPricingList.controls.map(customGroupToValue),
                pricingList: this.form.controls.pricingList.controls.map(tierGroupToValue),
                isPurchasable: value.isPurchasable,
            });
        }
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }], { relativeTo: this.route.parent?.parent });
    }
}
